import { useState } from "react";
import { Mail, MapPin, Phone, MessageCircle, Loader2, Clock, ShieldCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { COMPANY } from "../lib/brand";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", company: "", mobile: "", email: "",
    product_name: "", quantity: "", requirement: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/rfq", form);
      toast.success("Message sent. We'll respond within 24 hours.");
      setForm({ name: "", company: "", mobile: "", email: "", product_name: "", quantity: "", requirement: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      {/* HERO */}
      <section className="bg-carbon-950 text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
          <div className="overline text-amber-400 mb-3">Get in Touch</div>
          <h1 className="h-display max-w-3xl">Talk to engineering directly.</h1>
          <p className="body-lead text-slate-300 mt-4 max-w-2xl">
            Mining safety equipment supply, installation, calibration, or service —
            our team responds within 24 hours.
          </p>
        </div>
      </section>

      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-14 md:py-20 grid lg:grid-cols-12 gap-8 lg:gap-10">
        {/* LEFT — contact info cards */}
        <div className="lg:col-span-5 space-y-4">
          {COMPANY.units.map((u, idx) => (
            <div key={idx} className="card-premium p-6" data-testid={`contact-unit-${idx}`}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-7 h-7 text-amber-500" strokeWidth={2} />
                <span className="text-[10px] tracking-[0.25em] text-amber-600 font-bold uppercase font-mono bg-amber-50 px-2 py-1">
                  {u.label}
                </span>
              </div>
              <div className="font-heading font-bold text-base text-carbon mb-1">{u.lines[0]}</div>
              <p className="text-slate-700 leading-relaxed text-sm">
                {u.lines.slice(1).join(", ")}
              </p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(u.mapQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`contact-directions-${idx}`}
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-amber-600 hover:text-amber-700"
              >
                Get Directions →
              </a>
            </div>
          ))}

          <div className="card-premium p-6">
            <Phone className="w-7 h-7 text-amber-500 mb-3" strokeWidth={2} />
            <div className="overline mb-2">Phone</div>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="contact-phone"
              className="block text-carbon hover:text-amber-600 font-mono text-base font-bold"
            >
              {COMPANY.phone}
            </a>
            <a
              href={`tel:${COMPANY.phone2Raw}`}
              data-testid="contact-phone-2"
              className="block text-carbon hover:text-amber-600 font-mono text-base font-bold mt-1"
            >
              {COMPANY.phone2}
            </a>
            <p className="text-xs text-slate-500 mt-2">Mon–Sat, 9 AM – 7 PM IST</p>
          </div>

          <div className="card-premium p-6">
            <Mail className="w-7 h-7 text-amber-500 mb-3" strokeWidth={2} />
            <div className="overline mb-2">Email</div>
            <a
              href={`mailto:${COMPANY.email1}`}
              data-testid="contact-email-1"
              className="block text-carbon hover:text-amber-600 font-mono text-sm font-bold"
            >
              {COMPANY.email1}
            </a>
            {COMPANY.email2 && COMPANY.email2 !== COMPANY.email1 && (
              <a
                href={`mailto:${COMPANY.email2}`}
                data-testid="contact-email-2"
                className="block text-carbon hover:text-amber-600 font-mono text-sm font-bold mt-1"
              >
                {COMPANY.email2}
              </a>
            )}
          </div>

          <a
            href={`https://wa.me/${COMPANY.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-wa-btn"
            className="block bg-[#25D366] text-white p-5 hover:bg-[#1ebe5b] transition-colors"
          >
            <MessageCircle className="w-7 h-7 mb-2" strokeWidth={2} />
            <div className="font-bold text-sm uppercase tracking-[0.18em]">Chat on WhatsApp</div>
            <div className="text-xs opacity-90 mt-1">Instant response from our sales team</div>
          </a>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="border border-slate-200 p-4 text-center">
              <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
              <div className="font-mono text-[10px] tracking-widest text-slate-500 font-bold">RESPONSE SLA</div>
              <div className="font-heading font-extrabold text-lg text-carbon">24 hrs</div>
            </div>
            <div className="border border-slate-200 p-4 text-center">
              <ShieldCheck className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
              <div className="font-mono text-[10px] tracking-widest text-slate-500 font-bold">GSTIN</div>
              <div className="font-mono font-extrabold text-[11px] text-carbon mt-1 break-all">{COMPANY.gst}</div>
            </div>
          </div>
        </div>

        {/* RIGHT — enquiry form */}
        <div className="lg:col-span-7">
          <div className="border border-slate-200 p-6 md:p-8 bg-white shadow-[0_22px_44px_-22px_rgba(10,19,34,0.18)]">
            <div className="overline mb-2">Send Enquiry</div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight mb-6 text-carbon">
              We respond within 24 hours.
            </h2>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Full Name *</Label>
                <Input data-testid="contact-name" required value={form.name} onChange={setField("name")} className="rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Company</Label>
                <Input data-testid="contact-company" value={form.company} onChange={setField("company")} className="rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Mobile *</Label>
                <Input data-testid="contact-mobile" required value={form.mobile} onChange={setField("mobile")} className="rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Email *</Label>
                <Input data-testid="contact-email" type="email" required value={form.email} onChange={setField("email")} className="rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Product / Service</Label>
                <Input data-testid="contact-product" value={form.product_name} onChange={setField("product_name")} className="rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Quantity</Label>
                <Input data-testid="contact-qty" value={form.quantity} onChange={setField("quantity")} className="rounded-none h-11" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Message *</Label>
                <Textarea data-testid="contact-requirement" required rows={5} value={form.requirement} onChange={setField("requirement")} className="rounded-none" />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button data-testid="contact-submit" type="submit" disabled={submitting} className="rounded-full btn-amber h-12 px-8 font-bold uppercase tracking-[0.18em] text-xs">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Send Enquiry
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* MAPS — both unit locations */}
      <section className="border-t border-slate-200">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="overline text-center mb-2">Locate Us</div>
          <h2 className="h-section text-carbon text-center mb-8">Two Bengaluru offices, one team.</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {COMPANY.units.map((u, idx) => (
              <div key={idx} className="border border-slate-200 bg-white">
                <div className="bg-carbon-950 text-white p-4">
                  <div className="text-[10px] tracking-[0.25em] text-amber-400 font-bold uppercase font-mono mb-1">
                    {u.label}
                  </div>
                  <div className="text-sm leading-snug">{u.lines.slice(1).join(", ")}</div>
                </div>
                <div className="aspect-[16/9] w-full">
                  <iframe
                    title={`${u.label} — Map`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(u.mapQuery)}&output=embed`}
                    className="w-full h-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
